import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, RefreshCw, AlertTriangle, ChevronRight, Eye, X,
} from 'lucide-react';
import { dashboardApi, dashFilterExtra } from '../../services/dashboard-api.service';
import FilterBar from '../../components/dashboard-report/FilterBar';
import type {
  DashboardAbstract as DashboardAbstractType,
  DashboardFilters,
  PendingTaskGroup,
  WorkDetailRow,
} from '../../types/dashboard.types';

// ==========================================
// WORK DETAIL TABLE (matches each Excel sheet's "Work wise format")
// ==========================================

interface WorkDetailTableProps {
  taskType: string;
  data: PendingTaskGroup[];
  onClose: () => void;
}

// Column config per task type (matches each Excel sheet exactly)
const DETAIL_COLUMNS: Record<string, string[]> = {
  default: ['Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work', 'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length', 'Stage of work', 'Detailed Remarks'],
  'cr-approvals': ['Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work', 'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length', 'Cumulative expenditure', 'Completed Length', 'Stage of work', 'Detailed Remarks'],
  'final-payment': ['Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work', 'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length', 'Cumulative expenditure', 'Completed Length', 'Stage of work', 'Detailed Remarks'],
  'pending-updation': ['Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work', 'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length', 'Cumulative expenditure', 'Completed Length', 'Stage of work', 'Detailed Remarks'],
  'eoat': ['Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work', 'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length', 'Date of Agmt.', 'Date set for completion', 'Cumulative expenditure', 'Completed Length', 'Stage of work', 'Detailed Remarks'],
  'forest-clearance': ['Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work', 'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length', 'Cumulative expenditure', 'Completed Length', 'Stage of work', 'Detailed Remarks'],
};

function getCellValue(col: string, w: WorkDetailRow, taskType: string): React.ReactNode {
  switch (col) {
    case 'Work ID': return w.work_id;
    case 'District': return w.district_name;
    case 'Name of the work': return <span className="max-w-[220px] block truncate" title={w.work_name}>{w.work_name}</span>;
    case 'Type of Work': return w.work_type || '-';
    case 'Grant': return w.grant_type;
    case 'Year of Sanction': return w.oas_sanction_date || '-';
    case 'Estimate Cost': return w.estimate_cost.toFixed(2);
    case 'Sanctioned Length': return w.length_sanctioned_km;
    case 'Cumulative expenditure': return w.cum_exp_on_work.toFixed(2);
    case 'Completed Length': return w.cumulative_length_completed;
    case 'Date of Agmt.': return w.agreement_date || '-';
    case 'Date set for completion':
      return <span className={taskType === 'eoat' ? 'text-red-600 font-semibold' : ''}>{w.completion_date_set || '-'}</span>;
    case 'Stage of work': return w.sub_stage_of_work || w.abstract_stage_of_work;
    case 'Detailed Remarks': return <span className="max-w-[150px] block truncate" title={w.detailed_stage_of_work}>{w.detailed_stage_of_work || '-'}</span>;
    default: return '-';
  }
}

const WorkDetailTable: React.FC<WorkDetailTableProps> = ({ taskType, data, onClose }) => {
  const columns = DETAIL_COLUMNS[taskType] || DETAIL_COLUMNS.default;
  const allWorks = data.flatMap(g => g.works);
  const totalWorks = data.reduce((s, g) => s + g.work_count, 0);
  const totalCost = data.reduce((s, g) => s + g.estimate_cost_lakhs, 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 pt-8 pb-20">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-[95vw] w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-blue-600 text-white rounded-t-lg">
            <div>
              <h3 className="text-lg font-semibold">{TASK_CONFIG[taskType]?.title || taskType}</h3>
              <p className="text-sm text-blue-100">{TASK_CONFIG[taskType]?.description || ''}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-blue-200"><X className="h-5 w-5" /></button>
          </div>

          {/* Grant Summary Table */}
          <div className="px-6 py-3 bg-gray-50 border-b">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-700 font-semibold border-b-2 border-gray-300">
                  <th className="py-2 w-16">Sno</th>
                  <th className="py-2">Grant</th>
                  <th className="py-2 text-right">Number of works</th>
                  <th className="py-2 text-right">Estimate cost (in Lakhs)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((g, i) => (
                  <tr key={g.grant_type} className="border-b border-gray-200">
                    <td className="py-1.5">{i + 1}</td>
                    <td className="py-1.5 font-medium">{g.grant_type}</td>
                    <td className="py-1.5 text-right">{g.work_count}</td>
                    <td className="py-1.5 text-right">{g.estimate_cost_lakhs.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-400 font-bold">
                  <td className="py-2" colSpan={2}>Total</td>
                  <td className="py-2 text-right">{totalWorks}</td>
                  <td className="py-2 text-right">{totalCost.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Work wise format */}
          <div className="flex-1 overflow-auto">
            <div className="px-6 py-2 bg-gray-100 border-b">
              <h4 className="text-sm font-bold text-gray-700">Work wise format</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {columns.map(h => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap border-b border-gray-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {allWorks.map((w, idx) => (
                    <tr key={`${w.work_id}-${idx}`} className="hover:bg-blue-50">
                      <td className="px-2 py-1.5">{idx + 1}</td>
                      {columns.slice(1).map(col => (
                        <td key={col} className="px-2 py-1.5 whitespace-nowrap">{getCellValue(col, w, taskType)}</td>
                      ))}
                    </tr>
                  ))}
                  {allWorks.length === 0 && (
                    <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400">No works found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// TASK CONFIG (matches Excel Abs sheet exactly)
// ==========================================

const TASK_CONFIG: Record<string, { title: string; description: string; key: string }> = {
  'ts-to-be-accorded': { title: 'TS to be accorded', description: 'All works whose Sub Stage is "NS- Estimate Stage"', key: 'ts_to_be_accorded' },
  'tender-stage': { title: 'Tender Stage', description: 'All works whose Sub Stage is "NS- Tender Stage"', key: 'tender_stage' },
  'agreements': { title: 'Agreements to be concluded', description: 'All works whose Sub Stage is "NS- Agreement Stage"', key: 'agreements_pending' },
  'cr-approvals': { title: 'CR Approvals pending for Physically completed work', description: 'All works whose Sub Stage is "Physically Completed (CR Not Approved)"', key: 'cr_approvals_pending' },
  'final-payment': { title: 'FPP - Final payments pending for CR approved works', description: 'All works whose Sub Stage is "CR Approved & Final Bill Pending"', key: 'final_payment_pending' },
  'pending-updation': { title: 'Pending updation of Physical & Financial progress of last month', description: 'All works whose Sub Stage is Blank or not updated this month', key: 'pending_updation' },
  'eoat': { title: 'EOATs - Status To be updated for work completion date is expired', description: 'Works where completion date < system date and stage ≠ Completed', key: 'eoat_works' },
  'forest-clearance': { title: 'Works held up due to Forest Clearance', description: 'All works whose Sub Stage is "Held Up- Forest clearance"', key: 'forest_clearance' },
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const DashboardAbstract: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardAbstractType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>({ grant: 'All' });

  // Detail modal state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailType, setDetailType] = useState('');
  const [detailData, setDetailData] = useState<PendingTaskGroup[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { grant, districtId, mandalId } = filters;
      const g = grant === 'All' ? undefined : grant;
      const result = await dashboardApi.getAbstract(g, districtId, mandalId, dashFilterExtra(filters));
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const { grant, districtId, mandalId } = filters;
      await dashboardApi.exportExcel(grant === 'All' ? undefined : grant, districtId, mandalId);
    } catch { setError('Export failed'); }
    finally { setExporting(false); }
  };

  const openDetail = (taskType: string) => {
    if (!data) return;
    const key = TASK_CONFIG[taskType]?.key;
    if (!key) return;
    const taskData = (data.pending_tasks as any)[key] || [];
    setDetailType(taskType);
    setDetailData(taskData);
    setDetailOpen(true);
  };

  const ws = data?.workload_summary;
  const sc = data?.summary_counts;
  const ds = data?.design_status;
  const qc = data?.qc_summary;
  const bg = data?.bg_lapse_status;
  const or = data?.other_reports;
  const asOnDate = data?.as_on_date || new Date().toISOString().split('T')[0];

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Title - matches Row 0 */}
      <div className="mb-4">
        <FilterBar
          filters={filters}
          onFiltersChange={setFilters}
          onExport={handleExport}
          onRefresh={loadData}
          loading={loading}
          exporting={exporting}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
          <button onClick={loadData} className="text-red-600 hover:text-red-800 underline text-sm">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Loading dashboard data...</span>
        </div>
      ) : data && (
        <div className="space-y-6">
          {/* ============================================ */}
          {/* SECTION 1: ABSTRACT STATUS (Abs Row 1) */}
          {/* ============================================ */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-blue-700 text-white px-6 py-3">
              <h2 className="text-base font-bold">ABSTRACT STATUS OF PRED WORKS DURING THIS FINANCIAL YEAR AS ON {asOnDate}</h2>
            </div>

            {/* Workload Summary - matches Abs Rows 4-6 */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-blue-800">Total work load<br/><span className="text-xs font-normal">(Spill over + New sanctions)</span></th>
                    <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-green-800">Completed</th>
                    <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-orange-800">Progress</th>
                    <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-red-800">Not Started</th>
                  </tr>
                  <tr className="bg-gray-50 text-xs text-gray-600">
                    <th className="px-3 py-1.5 border text-center">Nos</th>
                    <th className="px-3 py-1.5 border text-center">Length</th>
                    <th className="px-3 py-1.5 border text-center">Est</th>
                    <th className="px-3 py-1.5 border text-center">Nos</th>
                    <th className="px-3 py-1.5 border text-center">Length</th>
                    <th className="px-3 py-1.5 border text-center">Est</th>
                    <th className="px-3 py-1.5 border text-center">Nos</th>
                    <th className="px-3 py-1.5 border text-center">Length</th>
                    <th className="px-3 py-1.5 border text-center">Est</th>
                    <th className="px-3 py-1.5 border text-center">Nos</th>
                    <th className="px-3 py-1.5 border text-center">Length</th>
                    <th className="px-3 py-1.5 border text-center">Est</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center font-bold text-lg">
                    <td className="px-3 py-3 border text-blue-700">{ws?.total.count || 0}</td>
                    <td className="px-3 py-3 border text-blue-700">{(ws?.total.length_km || 0).toFixed(1)}</td>
                    <td className="px-3 py-3 border text-blue-700">{(ws?.total.estimate_cost_lakhs || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 border text-green-700">{ws?.completed.count || 0}</td>
                    <td className="px-3 py-3 border text-green-700">{(ws?.completed.length_km || 0).toFixed(1)}</td>
                    <td className="px-3 py-3 border text-green-700">{(ws?.completed.estimate_cost_lakhs || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 border text-orange-700">{ws?.in_progress.count || 0}</td>
                    <td className="px-3 py-3 border text-orange-700">{(ws?.in_progress.length_km || 0).toFixed(1)}</td>
                    <td className="px-3 py-3 border text-orange-700">{(ws?.in_progress.estimate_cost_lakhs || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 border text-red-700">{ws?.not_started.count || 0}</td>
                    <td className="px-3 py-3 border text-red-700">{(ws?.not_started.length_km || 0).toFixed(1)}</td>
                    <td className="px-3 py-3 border text-red-700">{(ws?.not_started.estimate_cost_lakhs || 0).toFixed(2)}</td>
                  </tr>
                  {/* Row 7: View more links */}
                  <tr className="text-center text-xs">
                    <td colSpan={3} className="px-3 py-1.5 border">
                      <a href="/works/list" className="text-blue-600 hover:underline cursor-pointer">View more</a>
                    </td>
                    <td colSpan={3} className="px-3 py-1.5 border">
                      <a href="/works/list?stage=Completed" className="text-green-600 hover:underline cursor-pointer">View more</a>
                    </td>
                    <td colSpan={3} className="px-3 py-1.5 border">
                      <a href="/works/list?stage=In Progress" className="text-orange-600 hover:underline cursor-pointer">View more</a>
                    </td>
                    <td colSpan={3} className="px-3 py-1.5 border">
                      <a href="/works/list?stage=Not Started" className="text-red-600 hover:underline cursor-pointer">View more</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ============================================ */}
          {/* SECTION 2: PENDING TASKS (Abs Rows 10-14) */}
          {/* ============================================ */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-blue-700 text-white px-6 py-3">
              <h2 className="text-base font-bold">PENDING TASKS TO BE MONITORED STATUS AS ON {asOnDate}</h2>
            </div>

            {/* Row 1: TS, Design, Tender, Agreements, CR, FPP */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('ts-to-be-accorded')}>
                      TS to be accorded
                    </th>
                    <th colSpan={2} className="px-3 py-2 border text-center font-semibold text-gray-800">
                      Design status
                    </th>
                    <th colSpan={2} className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('tender-stage')}>
                      Tender Status
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('agreements')}>
                      Agreements to be concluded
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('cr-approvals')}>
                      CR Approvals pending
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('final-payment')}>
                      FPP - Final payments pending
                    </th>
                  </tr>
                  <tr className="bg-gray-50 text-xs text-gray-500">
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center">Total Bridges<br/>(Spill over+New)</td>
                    <td className="px-3 py-1 border text-center">Designs Approved</td>
                    <td className="px-3 py-1 border text-center">Tenders to be Called</td>
                    <td className="px-3 py-1 border text-center">Tenders Under process</td>
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center">
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-blue-700 cursor-pointer hover:underline" onClick={() => openDetail('ts-to-be-accorded')}>{sc?.ts_to_be_accorded || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-gray-700">{ds?.total_bridges_during_year || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-green-700">{ds?.designs_approved || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-orange-700 cursor-pointer hover:underline" onClick={() => openDetail('tender-stage')}>{sc?.tender_stage || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-gray-600">{sc?.tender_under_process || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-purple-700 cursor-pointer hover:underline" onClick={() => openDetail('agreements')}>{sc?.agreements_pending || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-green-700 cursor-pointer hover:underline" onClick={() => openDetail('cr-approvals')}>{sc?.cr_approvals_pending || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-teal-700 cursor-pointer hover:underline" onClick={() => openDetail('final-payment')}>{sc?.final_payment_pending || 0}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Row 2: Pending updation, QC, BG, EOAT, Forest, Other */}
            <div className="overflow-x-auto border-t">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('pending-updation')}>
                      Pending updation of Physical & Financial progress
                    </th>
                    <th colSpan={2} className="px-3 py-2 border text-center font-semibold text-gray-800">
                      QC Reports
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800">
                      BG to be lapsed within 15 days / lapsed
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('eoat')}>
                      EOATs Status
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800 cursor-pointer hover:bg-blue-50" onClick={() => openDetail('forest-clearance')}>
                      Forest clearance
                    </th>
                    <th className="px-3 py-2 border text-center font-semibold text-gray-800">
                      Other reports
                    </th>
                  </tr>
                  <tr className="bg-gray-50 text-xs text-gray-500">
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center">Works to be inspected</td>
                    <td className="px-3 py-1 border text-center">Works inspected</td>
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center">&nbsp;</td>
                    <td className="px-3 py-1 border text-center text-[10px]">Court | Arbitration | V&E | Maintenance DLP</td>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center">
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-gray-700 cursor-pointer hover:underline" onClick={() => openDetail('pending-updation')}>{sc?.pending_updation || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-gray-700">{qc?.works_to_be_inspected || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-gray-700">{qc?.works_inspected || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-amber-700">{(bg?.lapsing_within_15_days?.count || 0) + (bg?.already_lapsed?.count || 0)}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-red-700 cursor-pointer hover:underline" onClick={() => openDetail('eoat')}>{sc?.eoat_works || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <span className="text-2xl font-bold text-green-800 cursor-pointer hover:underline" onClick={() => openDetail('forest-clearance')}>{sc?.forest_clearance || 0}</span>
                    </td>
                    <td className="px-3 py-3 border">
                      <div className="flex justify-center gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-red-600">{or?.court_cases || 0}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-600">{or?.arbitration_cases || 0}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-600">{or?.ve_cases || 0}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">{or?.maintenance_dlp || 0}</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailOpen && (
        <WorkDetailTable
          taskType={detailType}
          data={detailData}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardAbstract;
